import { Checkbox, TableCellProps, TableRow, Theme } from '@mui/material';
import { groupBy } from 'lodash';
import { Fragment } from 'react';
import { EMPTY_VALUE } from 'shared/constants';
import {
  CheckboxOnChangeEvent,
  Column,
  TableRowOnClickEvent,
} from 'shared/types';
import { StyledDivider, StyledRowCell } from '../styles';
import { OptionalIds, TableProps } from '../types';
import { formatNumberWithSpaces } from '../../../../../common/BudgetTable/utils/formatNumberWithSpaces';

type Cell =
  | string
  | number
  | boolean
  | { type: 'budget'; value: number; currency: string }
  | { type: 'performance'; dsp: number; adServer: number };

const renderCell = ({
  format,
  cell,
  renderer,
}: Partial<Column> & {
  cell: Cell;
}) => {
  if (typeof cell === 'object' && cell !== null) {
    if (cell.type === 'budget') {
      return formatNumberWithSpaces(cell.value.toFixed(0));
    }

    if (cell.type === 'performance') {
      return `DSP: ${cell.dsp}, AdServer: ${cell.adServer}`;
    }
  }

  const value = cell || EMPTY_VALUE;

  if (format) {
    return format(value, 'PLN');
  }
  if (renderer) {
    return renderer(cell);
  }
  return value;
};

export const renderRows = ({
  handleRowClick,
  optionalIds,
  theme,
  rows,
  columns,
}: {
  rows: TableProps['rows'];
  columns: TableProps['columns'];
  theme: Theme;
  optionalIds: OptionalIds;
  handleRowClick: (
    e: TableRowOnClickEvent | CheckboxOnChangeEvent,
    rowId: string
  ) => void;
}) => {
  const groupedRows = groupBy(rows, 'touchpointName');
  return Object.entries(groupedRows).flatMap(([_, groupRows], index) => {
    const rowColor =
      index % 2 === 0
        ? theme.palette.tableBackground.main
        : theme.palette.common.white;

    return groupRows.map((row) => (
      <TableRow
        key={row.touchpointName + row.touchpointFunnel}
        onClick={(e) =>
          handleRowClick(e, row.touchpointName + row.touchpointFunnel)
        }
        sx={{ backgroundColor: rowColor }}
      >
        <StyledRowCell padding="checkbox">
          <Checkbox
            color="primary"
            checked={
              row.isMandatory ||
              optionalIds[row.touchpointName + row.touchpointFunnel] ||
              false
            }
            disabled={row.isMandatory}
            onChange={(e) =>
              handleRowClick(e, row.touchpointName + row.touchpointFunnel)
            }
          />
        </StyledRowCell>
        {columns.map(({ key, dividerColumn, format, align, renderer }) => {
          return (
            <Fragment
              key={`${row.touchpointName + row.touchpointFunnel}${key}`}
            >
              <StyledRowCell align={align as TableCellProps['align']}>
                {renderCell({
                  format,
                  cell:
                    key === 'budget'
                      ? ({
                          type: 'budget',
                          value: row[key].value,
                          currency: row[key].currency,
                        } as unknown as Cell)
                      : (row[key] as Cell),
                  renderer,
                })}
              </StyledRowCell>
              {dividerColumn && (
                <StyledRowCell sx={{ padding: 0 }}>
                  <StyledDivider orientation="vertical" />
                </StyledRowCell>
              )}
            </Fragment>
          );
        })}
      </TableRow>
    ));
  });
};
